import { createSlice } from '@reduxjs/toolkit';
import { FETCH_STATUS } from '../../types/enums/fetch-status.enum';
import snackbarUtils from '../../utils/SnackbarUtils';
import {
  addMemberToGroup,
  removeMemberFromGroup,
  updateGroupName,
  createNewGroup,
  getGroupsList,
  getGroupDetails,
  deleteGroup,
} from './supportAdminPanel.action';

interface UserMember {
  id: number;
  name: string;
  email: string;
}

interface GroupData {
  id: number;
  name: string;
  assignees: number;
  members: UserMember[];
}

export interface SupportAdminPanelState {
  priorityGroups: GroupData[];
  groupsList: GroupData[];
  groupDetails: GroupData | null;
  addMemberStatus: FETCH_STATUS;
  removeMemberStatus: FETCH_STATUS;
  updateGroupStatus: FETCH_STATUS;
  createGroupStatus: FETCH_STATUS;
  saveGroupsStatus: FETCH_STATUS;
  getGroupsListStatus: FETCH_STATUS;
  getGroupDetailsStatus: FETCH_STATUS;
  deleteGroupStatus: FETCH_STATUS;
}

const initialState: SupportAdminPanelState = {
  priorityGroups: [],
  groupsList: [],
  groupDetails: null,

  addMemberStatus: FETCH_STATUS.NULL,
  removeMemberStatus: FETCH_STATUS.NULL,
  updateGroupStatus: FETCH_STATUS.NULL,
  createGroupStatus: FETCH_STATUS.NULL,
  saveGroupsStatus: FETCH_STATUS.NULL,
  getGroupsListStatus: FETCH_STATUS.NULL,
  getGroupDetailsStatus: FETCH_STATUS.NULL,
  deleteGroupStatus: FETCH_STATUS.NULL,
};

const supportAdminPanelSlice = createSlice({
  name: 'supportAdminPanel',
  initialState,
  reducers: {
    addMember: (state, action) => {
      const { groupId, member } = action.payload;
      state.priorityGroups = state.priorityGroups.map(group =>
        group.id === groupId
          ? {
              ...group,
              members: group.members.find(m => m.id === member.id) ? group.members : [...group.members, member],
              assignees: group.members.find(m => m.id === member.id) ? group.assignees : group.assignees + 1,
            }
          : group
      );
    },

    removeMember: (state, action) => {
      const { groupId, memberId } = action.payload;
      state.priorityGroups = state.priorityGroups.map(group =>
        group.id === groupId
          ? {
              ...group,
              members: group.members.filter(member => member.id !== memberId),
              assignees: group.members.filter(member => member.id !== memberId).length,
            }
          : group
      );
      snackbarUtils.success('Member removed from group successfully!');
    },

    updateGroup: (state, action) => {
      const { groupId, name } = action.payload;
      state.priorityGroups = state.priorityGroups.map(group => (group.id === groupId ? { ...group, name } : group));
    },

    addNewGroup: (state, action) => {
      const newGroup = action.payload;
      state.priorityGroups.push(newGroup);
    },

    removeGroup: (state, action) => {
      const { groupId, groupName } = action.payload;
      state.priorityGroups = state.priorityGroups.filter(group => group.id !== groupId);
      snackbarUtils.success(`Successfully deleted ${groupName}`);
    },
  },
  extraReducers: builder => {
    builder
      // Get Groups List
      .addCase(getGroupsList.pending, state => {
        state.getGroupsListStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getGroupsList.fulfilled, (state, action) => {
        state.getGroupsListStatus = FETCH_STATUS.FULFILLED;
        const convertedGroups =
          action.payload.items?.map(group => ({
            ...group,
            assignees: group.members?.length || 0,
            members:
              group.members?.map(member => ({
                id: member.id,
                name: `${member.firstname} ${member.lastname}`.trim(),
                email: member.email || '',
              })) || [],
          })) || [];
        state.groupsList = convertedGroups;
        state.priorityGroups = convertedGroups;
      })
      .addCase(getGroupsList.rejected, (state, action) => {
        state.getGroupsListStatus = FETCH_STATUS.REJECTED;
        snackbarUtils.error(action.payload as string);
      })

      // Get Group Details
      .addCase(getGroupDetails.pending, state => {
        state.getGroupDetailsStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getGroupDetails.fulfilled, (state, action) => {
        state.getGroupDetailsStatus = FETCH_STATUS.FULFILLED;
        const group = action.payload;
        const convertedGroup = {
          ...group,
          assignees: group.members?.length || 0,
          members:
            group.members?.map(member => ({
              id: member.id,
              name: `${member.firstname} ${member.lastname}`.trim(),
              email: member.email || '',
            })) || [],
        };
        state.groupDetails = convertedGroup;
      })
      .addCase(getGroupDetails.rejected, (state, action) => {
        state.getGroupDetailsStatus = FETCH_STATUS.REJECTED;
        snackbarUtils.error(action.payload as string);
      })

      // Create New Group
      .addCase(createNewGroup.pending, state => {
        state.createGroupStatus = FETCH_STATUS.PENDING;
      })
      .addCase(createNewGroup.fulfilled, (state, action) => {
        state.createGroupStatus = FETCH_STATUS.FULFILLED;
        const newGroup = {
          ...action.payload,
          assignees: action.payload.members?.length || 0,
          members:
            action.payload.members?.map(member => ({
              id: member.id,
              name: `${member.firstname} ${member.lastname}`.trim(),
              email: member.email || '',
            })) || [],
        };
        state.priorityGroups.push(newGroup);
        snackbarUtils.success('Group created successfully!');
      })
      .addCase(createNewGroup.rejected, (state, action) => {
        state.createGroupStatus = FETCH_STATUS.REJECTED;
        snackbarUtils.error(action.payload as string);
      })

      // Update Group
      .addCase(updateGroupName.pending, state => {
        state.updateGroupStatus = FETCH_STATUS.PENDING;
      })
      .addCase(updateGroupName.fulfilled, (state, action) => {
        state.updateGroupStatus = FETCH_STATUS.FULFILLED;
        const updatedGroup = action.payload;
        const convertedGroup = {
          ...updatedGroup,
          assignees: updatedGroup.members?.length || 0,
          members:
            updatedGroup.members?.map(member => ({
              id: member.id,
              name: `${member.firstname} ${member.lastname}`.trim(),
              email: member.email || '',
            })) || [],
        };
        state.priorityGroups = state.priorityGroups.map(group =>
          group.id === updatedGroup.id ? convertedGroup : group
        );
        snackbarUtils.success('Group updated successfully!');
      })
      .addCase(updateGroupName.rejected, (state, action) => {
        state.updateGroupStatus = FETCH_STATUS.REJECTED;
        snackbarUtils.error(action.payload as string);
      })

      // Delete Group
      .addCase(deleteGroup.pending, state => {
        state.deleteGroupStatus = FETCH_STATUS.PENDING;
      })
      .addCase(deleteGroup.fulfilled, state => {
        state.deleteGroupStatus = FETCH_STATUS.FULFILLED;
        snackbarUtils.success('Group deleted successfully!');
      })
      .addCase(deleteGroup.rejected, (state, action) => {
        state.deleteGroupStatus = FETCH_STATUS.REJECTED;
        snackbarUtils.error(action.payload as string);
      })

      // Add Member to Group
      .addCase(addMemberToGroup.pending, state => {
        state.addMemberStatus = FETCH_STATUS.PENDING;
      })
      .addCase(addMemberToGroup.fulfilled, (state, action) => {
        state.addMemberStatus = FETCH_STATUS.FULFILLED;
        // Update the specific group in place to maintain order
        const updatedGroup = action.payload;
        const convertedGroup = {
          ...updatedGroup,
          assignees: updatedGroup.members?.length || 0,
          members:
            updatedGroup.members?.map(member => ({
              id: member.id,
              name: `${member.firstname} ${member.lastname}`.trim(),
              email: member.email || '',
            })) || [],
        };

        state.priorityGroups = state.priorityGroups.map(group =>
          group.id === updatedGroup.id ? convertedGroup : group
        );
        snackbarUtils.success('Member added to group successfully!');
      })
      .addCase(addMemberToGroup.rejected, (state, action) => {
        state.addMemberStatus = FETCH_STATUS.REJECTED;
        snackbarUtils.error(action.payload as string);
      })

      // Remove Member from Group
      .addCase(removeMemberFromGroup.pending, state => {
        state.removeMemberStatus = FETCH_STATUS.PENDING;
      })
      .addCase(removeMemberFromGroup.fulfilled, (state, action) => {
        state.removeMemberStatus = FETCH_STATUS.FULFILLED;
        const updatedGroup = action.payload;
        const convertedGroup = {
          ...updatedGroup,
          assignees: updatedGroup.members?.length || 0,
          members:
            updatedGroup.members?.map(member => ({
              id: member.id,
              name: `${member.firstname} ${member.lastname}`.trim(),
              email: member.email || '',
            })) || [],
        };

        state.priorityGroups = state.priorityGroups.map(group =>
          group.id === updatedGroup.id ? convertedGroup : group
        );
        snackbarUtils.success('Member removed from group successfully!');
      })
      .addCase(removeMemberFromGroup.rejected, (state, action) => {
        state.removeMemberStatus = FETCH_STATUS.REJECTED;
        snackbarUtils.error(action.payload as string);
      });
  },
});

export default supportAdminPanelSlice.reducer;
